import React, { useEffect, useState } from "react";

import type { ClientId, CoClient } from "../../domain/types";
import {
  generateMonthCloseGroups,
  type GstFilingFrequency,
  type GstRegistrationType,
  type MonthCloseChecklistAnswer,
  type MonthCloseContext,
  type MonthCloseGroup,
  type MonthCloseItem,
  type MonthCloseSection,
} from "../../domain/monthClose";
import { getAnswer, persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { tallyBooksApi, type TallyBankRow } from "../../data/tallyBooksApi";
import { dbTrue, fetchZohoRecords, type ZohoRecordsDTO } from "../../data/zohoRecordsApi";
import { CoButton } from "../../components/Button";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

const ANSWER_KEY = "goLive.monthCloseChecklist";

/** The gst-review screen's answer — filing frequency per GSTIN. */
const GST_REVIEW_KEY = "verifySetup.gstReview";
type GstReviewDraft = Record<string, { filingFrequency?: GstFilingFrequency }>;

// What each section means, said once above its groups. Wording from the
// checklist doc; the behaviour it describes (gating, first-run-only) is the
// monthly run's job in the tenant, not this template's.
const SECTION_NOTES: Record<MonthCloseSection, string | null> = {
  "pre-close": "Runs first each month and blocks everything below it — reconciling an area before the bank is complete is wasted work.",
  "first-close": "Appears only in the first run after the account start date, then never again.",
  area: null,
  "close-out": "Opens only when every area item is done or overridden. This is sign-off, not accounting work.",
};

/**
 * Everything the generator can already know about this client. Registration
 * type comes from organisation-level flags because Zoho exposes nothing per
 * registration (same line gst-review draws); filing frequency is what the CA
 * said on that screen. Books not connected or not readable → GST shows
 * generically rather than guessing "not registered".
 */
function tallyRegistration(type: string): GstRegistrationType | undefined {
  switch (type.trim().toLowerCase()) {
    case "regular":
      return "regular";
    case "composition":
      return "composition";
    case "sez":
      return "sez";
    default:
      return undefined;
  }
}

function maskAccount(number: string): string {
  const digits = number.replace(/\s+/g, "");
  return digits.length >= 4 ? `••${digits.slice(-4)}` : "";
}

/**
 * Tally keeps one company GSTIN and the banks created during verify setup.
 * E-invoicing is not recorded on this draft, so the IRN line stays off.
 * Unregistered, or a blank GSTIN, leaves the GST area out.
 */
function buildTallyContext(
  client: CoClient,
  banks: TallyBankRow[],
  gstReview: GstReviewDraft,
  cfoModule: boolean
): MonthCloseContext {
  const gstin = client.tallyGstin.trim().toUpperCase();
  const registrationType = tallyRegistration(client.tallyGstRegistrationType);
  const gstRegistered = gstin !== "" && registrationType !== undefined;
  const state = client.tallyGstState.trim();

  return {
    books: "tally",
    gstRegistered,
    gstins: gstRegistered
      ? [
          {
            key: gstin,
            label: state !== "" ? `${gstin} (${state})` : gstin,
            registrationType,
            eInvoicing: false,
            filingFrequency: gstReview[gstin]?.filingFrequency,
          },
        ]
      : [],
    banks: banks
      .filter((bank) => bank.account_type !== "cash" && bank.name.trim() !== "")
      .map((bank) => ({
        id: bank.guid || bank.name.trim(),
        label: [bank.name.trim(), maskAccount(bank.account_number)].filter(Boolean).join(" "),
      })),
    cfoModule,
    bookkeepingType: client.bookkeepingType,
  };
}

function buildContext(
  records: ZohoRecordsDTO | null,
  gstReview: GstReviewDraft,
  cfoModule: boolean,
  bookkeepingType: MonthCloseContext["bookkeepingType"]
): MonthCloseContext {
  const org = records?.organisation ?? null;

  let registrationType: GstRegistrationType | undefined;
  if (org) {
    if (dbTrue(org.is_sez)) registrationType = "sez";
    else if (dbTrue(org.is_composition_scheme)) registrationType = "composition";
    else if (dbTrue(org.is_gst_registered)) registrationType = "regular";
  }

  const gstRegistered = org === null ? true : registrationType !== undefined;

  const gstins = (records?.gstins ?? [])
    .filter((gstin) => dbTrue(gstin.is_active) || gstin.is_active === null)
    .map((gstin) => {
      const key = gstin.gstin ?? gstin.branch_id;
      return {
        key,
        label: gstin.gstin ? `${gstin.gstin}${gstin.state ? ` (${gstin.state})` : ""}` : "",
        registrationType,
        // Null reads as OFF, which is the opposite of what this used to do.
        // The show-everything rule assumed null meant a failed read; in
        // practice it mostly means Zoho answered "off" for an organisation
        // with Locations on, whose payload names no registration for the
        // answer to be stamped onto. Defaulting to true then added
        // "all B2B invoices carry a valid IRN" to every monthly close, for
        // ever, for a feature the client's books say is switched off — and
        // `auto` items render without a ×, so the CA could not remove it.
        // A close item nobody can act on or delete is worse than a missing
        // one. See the same default on the GST Settings step.
        eInvoicing: dbTrue(gstin.is_einvoice_enabled),
        filingFrequency: gstReview[key]?.filingFrequency,
      };
    });

  const banks = (records?.bank_accounts ?? [])
    .filter((account) => dbTrue(account.is_active) && account.account_type !== "cash")
    .map((account) => ({
      id: account.account_id,
      label: [account.bank_name ?? account.account_name ?? "Bank account", account.masked_number]
        .filter(Boolean)
        .join(" "),
    }));

  return { books: "zoho", gstRegistered, gstins, banks, cfoModule, bookkeepingType };
}

function ItemRow({ entry, onRemove }: { entry: MonthCloseItem; onRemove?: () => void }) {
  return (
    <li className="flex items-start justify-between gap-3 py-1.5">
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm">{entry.text}</span>
          {entry.custom && <span className="text-xs text-muted-foreground">added by you</span>}
        </div>
        {entry.hint && <span className="text-xs text-muted-foreground">{entry.hint}</span>}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove "${entry.text}"`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ×
        </button>
      )}
    </li>
  );
}

function GroupCard({
  group,
  onAdd,
  onRemove,
}: {
  group: MonthCloseGroup;
  onAdd: (text: string) => void;
  onRemove: (itemId: string) => void;
}) {
  const [draft, setDraft] = useState("");

  function submit() {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft("");
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-4">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold">{group.title}</h3>
        {group.tag && (
          <span className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{group.tag}</span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {group.items.length} {group.items.length === 1 ? "item" : "items"}
        </span>
      </div>

      <ul className="flex flex-col divide-y">
        {group.items.map((entry) => (
          <ItemRow key={entry.id} entry={entry} onRemove={entry.auto ? undefined : () => onRemove(entry.id)} />
        ))}
      </ul>

      <div className="flex gap-2 pt-1">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={`Add an item to ${group.title}…`}
          className="h-9 flex-1 rounded-md border bg-background px-2 text-sm"
        />
        <CoButton variant="outline" onClick={submit} disabled={!draft.trim()}>
          Add
        </CoButton>
      </div>
    </div>
  );
}

// Section headings appear when the section changes walking the group list —
// the generator emits groups already in section order.
function sectionHeading(section: MonthCloseSection): string {
  if (section === "area") return "Accounting areas";
  if (section === "pre-close") return "Pre-close gate";
  if (section === "first-close") return "First close";
  return "Close-out";
}

export function MonthCloseChecklistStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "month-close-checklist");
  const answer = useCoAnswer<MonthCloseChecklistAnswer>(clientId, ANSWER_KEY);

  const [records, setRecords] = useState<ZohoRecordsDTO | null>(null);
  const [tallyBanks, setTallyBanks] = useState<TallyBankRow[]>([]);
  const [reading, setReading] = useState(true);
  const isTally = client?.backend.system === "tally";

  const generated = answer !== undefined && answer.groups.length > 0;

  useEffect(() => {
    if (!client) return;
    const tally = client.backend.system === "tally";
    let live = true;
    setReading(true);

    const finish = () => {
      if (live) setReading(false);
    };
    if (tally) {
      tallyBooksApi
        .bankState(clientId)
        .then((state) => {
          if (live) setTallyBanks(state.banks);
        })
        .catch(() => undefined)
        .finally(finish);
    } else {
      fetchZohoRecords(clientId, ["organisation", "gstins", "bank_accounts"])
        .then((result) => {
          if (live) setRecords(result);
        })
        // No books to read (upload-mode client) or a failed read both mean the
        // same thing here: generate from what the flow knows, show the rest.
        .catch(() => undefined)
        .finally(finish);
    }

    return () => {
      live = false;
    };
    // The books system decides which read to run. Client field edits do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, client?.backend.system]);

  // The template is the point of this page — build it as soon as the books
  // are read instead of asking for a click. Only fires for a client with
  // nothing saved yet; a saved template (with the CA's edits) never gets
  // overwritten on a revisit.
  useEffect(() => {
    if (!reading && !generated && client) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading, generated, clientId]);

  if (!client) return null;

  const clientName = client.name || "this client";

  function currentContext(): MonthCloseContext {
    const gstReview = getAnswer<GstReviewDraft>(clientId, GST_REVIEW_KEY) ?? {};
    const cfoModule = (client?.services ?? []).includes("cfo");
    if (isTally && client) {
      return buildTallyContext(client, tallyBanks, gstReview, cfoModule);
    }
    return buildContext(records, gstReview, cfoModule, client?.bookkeepingType);
  }

  function save(groups: MonthCloseGroup[], generatedAt?: string) {
    void persistAnswer<MonthCloseChecklistAnswer>(clientId, ANSWER_KEY, {
      generatedAt: generatedAt ?? answer?.generatedAt ?? new Date().toISOString(),
      groups,
    });
  }

  function generate() {
    save(generateMonthCloseGroups(currentContext()), new Date().toISOString());
  }

  function addItem(groupId: string, text: string) {
    if (!answer) return;
    save(
      answer.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              items: [...group.items, { id: `custom-${Date.now()}`, text, auto: false, custom: true }],
            }
          : group
      )
    );
  }

  function removeItem(groupId: string, itemId: string) {
    if (!answer) return;
    save(
      answer.groups.map((group) =>
        group.id === groupId ? { ...group, items: group.items.filter((entry) => entry.id !== itemId) } : group
      )
    );
  }

  return (
    <SubStepShell
      title="Month-close checklist"
      thenWhat={generated ? "Next: catch-up & training." : "Building the checklist…"}
      onContinue={() => void goNext()}
      continueDisabled={!generated}
    >
      {!generated ? (
        <div className="rounded-lg border border-dashed p-6">
          <p className="text-sm text-muted-foreground">
            Reading the books and building {clientName}'s checklist…
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {answer.groups.map((group, index) => {
            const previous = answer.groups[index - 1];
            const newSection = !previous || previous.section !== group.section;
            const note = SECTION_NOTES[group.section];
            return (
              <React.Fragment key={group.id}>
                {newSection && (
                  <div className="flex flex-col gap-0.5 pt-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {sectionHeading(group.section)}
                    </h3>
                    {note && <p className="text-xs text-muted-foreground">{note}</p>}
                  </div>
                )}
                <GroupCard
                  group={group}
                  onAdd={(text) => addItem(group.id, text)}
                  onRemove={(itemId) => removeItem(group.id, itemId)}
                />
              </React.Fragment>
            );
          })}
        </div>
      )}
    </SubStepShell>
  );
}
